import React from 'react'
import { Button } from "@progress/kendo-react-buttons";
import { Editor, EditorUtils, ProseMirror, EditorTools } from '@progress/kendo-react-editor'

import { accentUtils, formHelper, showDialog } from '../../services/HelperService'
import { AccentCodeDlg } from '../code/AccentCodeDlg';

const {
    Bold, Italic, Underline, Strikethrough, Subscript, Superscript,
    ForeColor, BackColor,
    AlignLeft, AlignCenter, AlignRight, AlignJustify,
    Indent, Outdent, OrderedList, UnorderedList,
    Undo, Redo, FontSize, FontName, FormatBlock,
    Link, Unlink, InsertImage,
    InsertTable,
    TableProperties, TableCellProperties,
    AddRowBefore, AddRowAfter, AddColumnBefore, AddColumnAfter,
    DeleteRow, DeleteColumn, DeleteTable,
    MergeCells, SplitCell
} = EditorTools;

const CustomForeColor = props => (
    <ForeColor {...props} colorPickerProps={{ view: 'gradient' }} />
);
const CustomBackColor = props => (
    <BackColor {...props} colorPickerProps={{ view: 'gradient' }} />
);

const ToggleTableColumnResize = ({ readOnly, enabled, onToggle }) => {
    return (
        <Button
            onClick={onToggle}
            disabled={readOnly}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title={enabled ? "Disable table column resizing" : "Enable table column resizing"}
        >
            {enabled ? "Resize Cols: On" : "Resize Cols: Off"}
        </Button>
    );
};

const CustomHtmlEditor = ({ view }) => {
    const onClick = async () => {
        const res = await showDialog(<AccentCodeDlg code={EditorUtils.getHtml(view.state)} />);
        if (res.canceled) return;

        EditorUtils.setHtml(view, res.code);
    };

    return (
        <Button
            onClick={onClick}
            onMouseDown={(e) => e.preventDefault()}
            onPointerDown={(e) => e.preventDefault()}
            title="View Html"
            icon="code"
        />
    );
};

export class AccentHTML extends React.Component {

    constructor(props) {
        super(props);


        this.ctrl = React.createRef();
        this.currentUserValue = this.props.model.getValue();
        this.state = {
            editorKey: 1,
            allowTableColumnResize: !!this.props.model.options?.htmlOption?.allowTableColumnResize,
        };

        this.onEditorBlur = this.onEditorBlur.bind(this);
        this.onMount = this.onMount.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.insertText = this.insertText.bind(this);
        this.toggleTableColumnResize = this.toggleTableColumnResize.bind(this);
        
    }


    isReadOnly() {
        return formHelper.isControlReadOnly(this.props.model);
    }

    onEditorBlur(_view, _domEvent){
        if (this.isReadOnly()) return;

        var me = this;    

        if (!accentUtils.isNull(me.props.model.options.onFocusLost)) {                
                me.props.model.options.onFocusLost({ selectionStart: _view.state.selection.from});
        }

        var newValue = this.ctrl.current.view.dom.innerHTML;

        this.currentUserValue = newValue;


        if (accentUtils.isNullOrEmpty(this.ctrl.current.view.dom.textContent)) {
            this.props.model.setValue(null);
        } else {
            this.props.model.setValue(newValue);
        }

        
        this.props.model.modified();


    }

    onMount(event){
        var me = this;
        event.dom.oncontextmenu = me.props.model.options.onContextMenu;

        const { EditorState } = ProseMirror;
        const { tableResizing } = EditorUtils;
        const { plugins, doc } = event.viewProps.state;
        const allowTableColumnResize = !!me.state.allowTableColumnResize;
        const nextPlugins = allowTableColumnResize
            ? [...tableResizing(), ...plugins]
            : plugins;

        return new ProseMirror.EditorView(
          { mount: event.dom }, {
            ...event.viewProps,
            editable: () => !me.isReadOnly(),
            state: EditorState.create({
                doc,
                plugins: nextPlugins
            }),

            handleDOMEvents: {
              ...(event.viewProps.handleDOMEvents || {}),
                'blur': me.onEditorBlur
            },
                handleKeyDown: me.onKeydown
          }
        );
    }

    insertText(pos, value) {
        if (this.isReadOnly()) return;

        var newState = this.ctrl.current.view.state.apply(this.ctrl.current.view.state.tr.insertText(value, pos));
        this.ctrl.current.view.updateState(newState);

        var newValue = this.ctrl.current.view.dom.innerHTML;

        this.props.model.setValue(newValue);
        this.props.model.modified();

        
        this.forceUpdate();

    }




    onChange(e) {
        if (this.isReadOnly()) return;

        this.props.model.modified();

    }


    onKeydown(e){
        if (this.isReadOnly()) return true;

        this.props.model.modified();
    }

    toggleTableColumnResize() {
        if (this.isReadOnly()) return;

        // Preserve the latest HTML into the model (Editor remount uses defaultContent).
        try {
            const html = this.ctrl.current?.view?.dom?.innerHTML;
            if (!accentUtils.isNull(html)) {
                if (accentUtils.isNullOrEmpty(this.ctrl.current.view.dom.textContent)) {
                    this.props.model.setValue(null);
                } else {
                    this.props.model.setValue(html);
                }
            }
        } catch { }

        this.setState(s => ({
            allowTableColumnResize: !s.allowTableColumnResize,
            editorKey: (s.editorKey ?? 0) + 1
        }));
    }
  

    render() {

        var modelValue = this.props.model.getValue();


        if (modelValue !== this.currentUserValue) { // model changed so hack     

            if (accentUtils.isNull(modelValue)) {
                modelValue = "";
            }
            this.currentUserValue = modelValue;
            this.ctrl.current.view.dom.innerHTML = modelValue;
        }

        var tools = undefined;
        const readOnly = this.isReadOnly();

        const allTools = (this.props.model.options.htmlOption) ? this.props.model.options.htmlOption.allTools : false;
        const minTools = (this.props.model.options.htmlOption) ? this.props.model.options.htmlOption.minTools : false;
        const height = (this.props.model.options.htmlOption) ? this.props.model.options.htmlOption.height : 500;


        const customTools = (this.props.model.options.htmlOption) ? this.props.model.options.htmlOption.customTools : null;

        if (allTools) {
            tools = [
                [Bold, Italic, Underline, Strikethrough],
                [Subscript, Superscript],
                [AlignLeft, AlignCenter, AlignRight, AlignJustify],
                [Indent, Outdent],
                [OrderedList, UnorderedList],
                [FontSize, FontName, FormatBlock, CustomForeColor, CustomBackColor],
                [Undo, Redo],
                [Link, Unlink, InsertImage, CustomHtmlEditor],
                [InsertTable],
                [TableProperties, TableCellProperties],
                [AddRowBefore, AddRowAfter, AddColumnBefore, AddColumnAfter],
                [DeleteRow, DeleteColumn, DeleteTable],
                [MergeCells, SplitCell],
                [p => <ToggleTableColumnResize {...p} enabled={this.state.allowTableColumnResize} onToggle={this.toggleTableColumnResize} />]
                ];
        } else {

            if (minTools) {
                tools =
                    [
                        [Bold, Italic, Underline, Strikethrough],
                        [OrderedList, UnorderedList],
                        [Undo]
                    ];

            } else {

                tools =
                    [
                        [Bold, Italic, Underline, Strikethrough],
                        [Subscript, Superscript],
                        [AlignLeft, AlignCenter, AlignRight, AlignJustify],
                        [Indent, Outdent],
                        [OrderedList, UnorderedList],
                        FontSize, FontName, FormatBlock,
                        [Undo, Redo],
                        [Link, Unlink]
                    ];

            }
        }


        if (!minTools && !accentUtils.isNull(customTools)) {
            tools.push(customTools);
        }

        if (accentUtils.isNull(modelValue)) {
            modelValue = "";
        }

        return (
            <div className="accentHtml">
                <Editor
                    ref={this.ctrl}
                    key={this.state.editorKey}
                    contentStyle={{ height: height }}
                    defaultContent={modelValue}
                    defaultEditMode="div"
                    change={this.onChange}
                    tools={readOnly ? [] : tools}
                    onMount={this.onMount}
                    onContextMenu={this.props.model.options.onContextMenu}
                />
            </div>
        );
    }

}
